// Confirmações e avisos no tema do sistema, no lugar do confirm()/alert() do
// navegador (caixa cinza, com o endereço do site no título e sem modo escuro).
// O ConfirmHost (montado no App) desenha a confirmação; os avisos usam o toast.
import { toast } from 'sonner';

export interface ConfirmOptions {
  title?: string;
  /** Texto do botão que executa a ação. Sem ele, sai do verbo da pergunta ("Excluir esta venda?" → "Excluir"). */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Botão vermelho. Sem ele, vale para perguntas de excluir, remover, desfazer etc. */
  danger?: boolean;
}

export interface ConfirmRequest {
  message: string;
  title: string;
  confirmLabel: string;
  cancelLabel: string;
  danger: boolean;
  resolve: (ok: boolean) => void;
}

let host: ((req: ConfirmRequest) => void) | null = null;

/** Usado só pelo ConfirmHost. Devolve a função que desliga o host. */
export function registerConfirmHost(fn: (req: ConfirmRequest) => void) {
  host = fn;
  return () => {
    if (host === fn) host = null;
  };
}

const DANGER = /\b(excluir|apagar|remover|revogar|estornar|desfazer|reverter|desativar|desconectar|desligar|limpar|cancelar|substituir)\b/i;

function verbFrom(message: string): string | null {
  const text = message.trim();
  const m = text.match(/deseja (?:realmente )?([a-zà-ú]+(?:ar|er|ir))\b/i) ?? text.match(/^([A-Za-zÀ-ú]+(?:ar|er|ir))\b/);
  if (!m || m[1].length <= 3) return null; // "dar baixa" etc. ficam com "Confirmar"
  const verb = m[1].toLowerCase();
  return verb.charAt(0).toUpperCase() + verb.slice(1);
}

export function confirmAction(message: string, options: ConfirmOptions = {}): Promise<boolean> {
  // Sem o host montado (testes, telas fora do App) continua o comportamento antigo.
  if (!host) return Promise.resolve(typeof window !== 'undefined' && typeof window.confirm === 'function' ? !!window.confirm(message) : false);
  const danger = options.danger ?? DANGER.test(message);
  const confirmLabel = options.confirmLabel ?? verbFrom(message) ?? 'Confirmar';
  const cancelLabel = options.cancelLabel ?? (confirmLabel.toLowerCase() === 'cancelar' ? 'Voltar' : 'Cancelar');
  const show = host;
  return new Promise(resolve => show({ message, title: options.title ?? 'Confirme para continuar', confirmLabel, cancelLabel, danger, resolve }));
}

const ERROR = /\b(erro|falha|falhou|não foi possível|nao foi possivel|inválid|invalid|obrigatóri|não coincidem|não pode|sem permissão)/i;
const SUCCESS = /\b(sucesso|salv[oa]s?|enviad[oa]s?|criad[oa]s?|atualizad[oa]s?|copiad[oa]s?|conclu[ií]d[oa]s?|registrad[oa]s?|importad[oa]s?)\b/i;

/** Aviso que não trava a tela. Erros em vermelho, confirmações de sucesso em verde. */
export function notify(message: unknown) {
  const text = String(message ?? '');
  const opts = { duration: Math.min(12000, 4000 + text.length * 40), style: { whiteSpace: 'pre-line' as const } };
  if (ERROR.test(text)) toast.error(text, opts);
  else if (SUCCESS.test(text)) toast.success(text, opts);
  else toast(text, opts);
}
